import type { IconType } from '@evisort/eds/types/SharedIconProps';

import { Icon } from '@evisort/eds/components/Icon/Icon';
import { StatusBadge } from '@evisort/eds/components/StatusBadge/StatusBadge';
import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import brandLockupSrc from '~/assets/images/evisort-workday-logo.svg';
import {
  hasActiveChild,
  testIsActiveLink,
} from '~/eds/src/modules/PageHeader/utils';
import { NavLink } from '~/eds/src/types/NavLink';
import { readCssDurationMs } from '~/features/app/readCssDurationMs';
import { getSidebarAnalyticsItem } from '~/features/app/sidebarAnalytics';
import { useSidebarNav } from '~/features/app/SidebarNavContext';
import {
  ASK_AI_PATHNAME,
  FEEDBACK_PATHNAME,
  PINNED_PATHNAME,
  SidebarNavControl,
} from '~/features/app/SidebarNavControl';
import { SidebarNavFlyout } from '~/features/app/SidebarNavFlyout';
import { SidebarPinButton } from '~/features/app/SidebarPinButton';
import {
  messages as pinnedMessages,
  SidebarPinnedSection,
} from '~/features/app/SidebarPinnedSection';
import { useNavigationItems } from '~/features/app/useNavigationItems';
import { useSidebarPinnedLinks } from '~/features/app/useSidebarPinnedLinks';
import { useAskAnything } from '~/features/ask-anything/ask-anything-button/useAskAnything';
import { useCandu } from '~/features/onboarding/Candu/useCandu';
import { CLMEvolutionFeedbackModal } from '~/features/workflows-clm-evolution/feedback/CLMEvolutionFeedbackModal';
import { messages as feedbackMessages } from '~/features/workflows-clm-evolution/feedback/messages';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { slices } from '~/redux/slices';
import search from '~/redux/slices/search';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import './AppSidebarNav.css';

// ─── i18n ─────────────────────────────────────────────────────────────────────

const messages = defineMessages({
  expandSection: {
    id: 'AppSidebarNav.expandSection',
    defaultMessage: 'Expand {label} section',
    description:
      'Accessible label for the chevron that expands a sidebar nav section.',
  },
  collapseSection: {
    id: 'AppSidebarNav.collapseSection',
    defaultMessage: 'Collapse {label} section',
    description:
      'Accessible label for the chevron that collapses a sidebar nav section.',
  },
  sidebarLandmark: {
    id: 'AppSidebarNav.sidebarLandmark',
    defaultMessage: 'sidebar navigation',
    description: 'Accessible name for the sidebar navigation landmark.',
  },
  mainNavLandmark: {
    id: 'AppSidebarNav.mainNavLandmark',
    defaultMessage: 'main navigation',
    description: 'Accessible name for the primary nav list inside the sidebar.',
  },
  goHome: {
    id: 'AppSidebarNav.goHome',
    defaultMessage: 'Go to home',
    description:
      'Accessible label for the logo button that navigates to the home page.',
  },
  workspaceLabel: {
    id: 'AppSidebarNav.workspaceLabel',
    defaultMessage: 'Workspace',
    description: 'Overline label above the sidebar nav sections.',
  },
  showSidebar: {
    id: 'AppSidebarNav.showSidebar',
    defaultMessage: 'Show sidebar',
    description:
      'Tooltip and a11y label for the sidebar toggle when the sidebar is collapsed.',
  },
  hideSidebar: {
    id: 'AppSidebarNav.hideSidebar',
    defaultMessage: 'Hide sidebar',
    description:
      'Tooltip and a11y label for the sidebar toggle when the sidebar is expanded.',
  },
  askAi: {
    id: 'AppSidebarNav.askAi',
    defaultMessage: 'Ask AI',
    description: 'Label for the sidebar entry that opens the AI chatbot.',
  },
  brandName: {
    id: 'AppSidebarNav.brandName',
    defaultMessage: 'Evisort',
    description:
      'Alt text for the Evisort brand lockup image in the mobile sidebar.',
  },
});

// ─── Icon mapping ─────────────────────────────────────────────────────────────

function getNavIcon(pathname: string): IconType {
  const p = pathname.toLowerCase();
  switch (true) {
    case p.startsWith('/automation'):
      return 'ai-custom-automation';
    case p.startsWith('/admin'):
      return 'settings-gear';
    case p.startsWith('/alerts'):
      return 'alert';
    case p.startsWith('/clause-library'):
      return 'library';
    case p.startsWith('/dashboards') || p.startsWith('/dashboard'):
      return 'dashboard';
    case p.startsWith('/document'):
      return 'document';
    case p.startsWith('/guidelines'):
      return 'library';
    case p.startsWith('/managed-entities') || p.startsWith('/parties'):
      return 'work-briefcase';
    case p.startsWith('/playbooks'):
      return 'library';
    case p.startsWith('/search'):
      return 'search';
    case p.startsWith('/saved-searches'):
      return 'bookmark';
    case p.startsWith('/upload'):
      return 'upload';
    case p.startsWith('/workflow'):
      return 'workflow';
    default:
      return 'document';
  }
}

// Behind FlagType.SidebarNav: swaps in the dedicated nav-only icon family
// (packages/eds `nav-*` entries) for the primary-nav rows it covers, leaving
// every other icon (Ask AI, Feedback, panel toggle) on the current EDS set.
const EXPLORATION_NAV_ICON: Partial<Record<IconType, IconType>> = {
  home: 'nav-home',
  document: 'nav-documents',
  workflow: 'nav-workflow',
  dashboard: 'nav-dashboard',
  'ai-custom-automation': 'nav-automation',
  library: 'nav-library',
  search: 'nav-search',
  'settings-gear': 'nav-admin',
};

// The current chevron hard-swaps between two glyphs on toggle (no transition
// is possible between two different icons). Keep the same resting-down/
// expanded-up direction, but get there by rotating a single chevron-down
// 180deg via CSS instead — the transition on .sidebar-chevron-btn
// .eds-icon--chevron-down (AppSidebarNav.css) animates it.
function resolveChevronIcon(
  hasSidebarNavUxExploration: boolean,
  isExpanded: boolean,
): IconType {
  if (hasSidebarNavUxExploration) return 'chevron-down';
  return isExpanded ? 'caret-up' : 'caret-down';
}

// ─── Disclosure ───────────────────────────────────────────────────────────────

/**
 * Keeps a block mounted while it animates closed, so collapsing a section plays
 * its exit instead of the sub-items vanishing on the next paint. The exit
 * duration comes from the stylesheet, which owns the timing.
 */
function useRevealPresence(
  isOpen: boolean,
  elementRef: RefObject<HTMLElement | null>,
) {
  const [isRendered, setIsRendered] = useState(isOpen);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsLeaving(false);
      setIsRendered(true);
      return;
    }
    if (!isRendered) return;

    setIsLeaving(true);
    const timeout = setTimeout(
      () => {
        setIsLeaving(false);
        setIsRendered(false);
      },
      readCssDurationMs(elementRef.current, '--sidebar-reveal-exit'),
    );
    return () => clearTimeout(timeout);
  }, [elementRef, isOpen, isRendered]);

  return { isLeaving, isRendered };
}

// ─── Overflow ─────────────────────────────────────────────────────────────────

/**
 * Tracks whether an element's content overflows it vertically. Used to drop
 * the collapsed rail's item labels once there's no room left to scroll them
 * comfortably, trading the label for vertical space instead.
 */
function useHasVerticalOverflow(
  ref: RefObject<HTMLElement | null>,
  isCollapsed: boolean,
  itemCount: number,
  pinnedCount: number,
  showAskAi: boolean,
  hasHomeLink: boolean,
) {
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const checkOverflow = () => {
      setHasOverflow(element.scrollHeight > element.clientHeight);
    };

    // The rail's own width animates on collapse/expand (`.sidebar-aside`'s
    // `--sidebar-collapse-duration`), which reflows the labels this measures —
    // an immediate read can catch that mid-transition. Wait for it to settle
    // first; the observer then catches anything that changes afterwards
    // (e.g. the window resizing) without an accompanying transition. The rail's
    // own box doesn't resize just because its content does (it's clipped by
    // `overflow-y: auto` inside a fixed-height flex column), so a content-count
    // change like a new pinned link needs this effect to rerun and recheck —
    // the observer alone would miss it.
    const settleTimeout = setTimeout(
      checkOverflow,
      readCssDurationMs(element, '--sidebar-collapse-duration'),
    );
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);
    return () => {
      clearTimeout(settleTimeout);
      resizeObserver.disconnect();
    };
  }, [ref, isCollapsed, itemCount, pinnedCount, showAskAi, hasHomeLink]);

  return hasOverflow;
}

/**
 * True for the one paint right after `value` changes, then false again.
 * A mobile/desktop crossing lands already collapsed, so the drawer's slide
 * transition needs to be off for that single paint or it visibly slides shut
 * instead of appearing hidden. `useLayoutEffect` sets the flag before paint so
 * it lands in the same frame as the class change; the plain `useEffect` below
 * clears it after paint, once that frame is done. Manual open/close toggles
 * never touch this flag, so they keep animating normally.
 */
function useSuppressTransitionOnChange<T>(value: T): boolean {
  const [suppress, setSuppress] = useState(false);
  const previousRef = useRef(value);

  useLayoutEffect(() => {
    if (previousRef.current === value) return;
    previousRef.current = value;
    setSuppress(true);
  }, [value]);

  useEffect(() => {
    if (suppress) setSuppress(false);
  }, [suppress]);

  return suppress;
}

// ─── Pinning ──────────────────────────────────────────────────────────────────

/** Pin state and its toggle, handed to every row that can carry a pin. */
export interface SidebarPinControl {
  isPinned: (pathname: string) => boolean;
  togglePin: (pathname: string) => void;
}

// ─── Sub-item component ───────────────────────────────────────────────────────

interface SidebarSubItemProps {
  link: NavLink;
  activePathname: string;
  onActivate: (path: string) => void;
  pinControl?: SidebarPinControl;
}

const SidebarSubItem = ({
  link,
  activePathname,
  onActivate,
  pinControl,
}: SidebarSubItemProps) => {
  const isActive = testIsActiveLink(activePathname)(link as NavLink);

  return (
    <div
      className={`sidebar-sub-item-row${isActive ? ' sidebar-sub-item-row--active' : ''}`}
    >
      <SidebarNavControl
        analyticsItem={getSidebarAnalyticsItem(link.pathname)}
        className="sidebar-sub-item-btn"
        isActive={isActive}
        pathname={link.pathname}
        onActivate={onActivate}
      >
        <span className="sidebar-sub-item-label">{link.label}</span>
        {link.badge && (
          <span className="sidebar-nav-item-badge">
            <StatusBadge status="informational" text={link.badge} />
          </span>
        )}
      </SidebarNavControl>
      {pinControl && (
        <SidebarPinButton
          isPinned={pinControl.isPinned(link.pathname)}
          label={link.label}
          onToggle={() => pinControl.togglePin(link.pathname)}
        />
      )}
    </div>
  );
};

// ─── Nav item component ───────────────────────────────────────────────────────

interface SidebarNavItemProps {
  link: NavLink;
  isCollapsed: boolean;
  activePathname: string;
  onActivate: (path: string) => void;
  /** Overrides the icon derived from the link's pathname (used for entries
   *  like Home / Ask AI that aren't part of the pathname → icon mapping). */
  icon?: IconType;
  analyticsSection?: string;
  testId?: string;
  /** Forwarded to the section's sub-items — a section root never pins. */
  pinControl?: SidebarPinControl;
  /** Count shown on the icon while the rail is collapsed, for a section whose
   *  contents are otherwise only visible in its flyout. */
  collapsedBadge?: string;
}

const SidebarNavItem = ({
  link,
  isCollapsed,
  activePathname,
  onActivate,
  icon: iconOverride,
  analyticsSection,
  testId,
  pinControl,
  collapsedBadge,
}: SidebarNavItemProps) => {
  const intl = useIntl();
  const isActive = testIsActiveLink(activePathname)(link);
  const anyChildActive = hasActiveChild(activePathname)(link);
  const isOwnLinkActive = isActive && !anyChildActive;
  const hasChildren = Boolean(link.children?.length);
  const hasSidebarNavUxExploration = useFlag(FlagType.SidebarNav);
  const baseIcon = iconOverride ?? getNavIcon(link.pathname);
  const explorationIcon = hasSidebarNavUxExploration
    ? EXPLORATION_NAV_ICON[baseIcon]
    : undefined;
  const icon = explorationIcon ?? baseIcon;
  const isIconFilled = explorationIcon !== undefined && isActive;
  const iconEl = (
    <Icon
      filled={isIconFilled}
      icon={icon}
      size={hasSidebarNavUxExploration ? 's' : undefined}
    />
  );
  // Ask AI's icon is a full-color brand mark with no background of its own —
  // give it the same gradient tile AskAnythingButton's cta-ai variant uses,
  // instead of floating bare against the row like the outline glyphs.
  const isAskAi = link.pathname === ASK_AI_PATHNAME;
  const iconContent =
    hasSidebarNavUxExploration && isAskAi ? (
      <span className="sidebar-nav-ask-ai-icon-tile">{iconEl}</span>
    ) : (
      iconEl
    );

  const [isExpanded, setIsExpanded] = useState(false);
  const childrenRef = useRef<HTMLDivElement>(null);
  const { isLeaving: isCollapsing, isRendered: areChildrenRendered } =
    useRevealPresence(isExpanded, childrenRef);

  // Auto-expand when active section changes
  useEffect(() => {
    if (isActive && hasChildren) {
      setIsExpanded(true);
    }
  }, [isActive, hasChildren]);

  if (isCollapsed) {
    const collapsedItem = (
      <SidebarNavControl
        analyticsItem={getSidebarAnalyticsItem(link.pathname)}
        analyticsSection={analyticsSection}
        className="sidebar-nav-collapsed-item"
        isActive={isActive}
        pathname={link.pathname}
        testId={testId}
        onActivate={onActivate}
      >
        <span aria-hidden="true" className="sidebar-nav-collapsed-icon">
          {iconContent}
          {collapsedBadge && (
            <span className="sidebar-nav-collapsed-badge">
              {collapsedBadge}
            </span>
          )}
        </span>
        <span className="sidebar-nav-collapsed-label">{link.label}</span>
      </SidebarNavControl>
    );

    // The rail has no room to expand a section inline, so its children live in
    // a hover/focus flyout instead of being dropped.
    if (!hasChildren) {
      return collapsedItem;
    }

    return (
      <SidebarNavFlyout
        activePathname={activePathname}
        label={link.label}
        links={link.children!}
        onActivate={onActivate}
      >
        {collapsedItem}
      </SidebarNavFlyout>
    );
  }

  // A group with sub-items becomes its own section (named after the parent
  // item) so the parent and its children all report that section instead of
  // the ambient one — the wrapper's attribute is only present when grouped,
  // so ungrouped items keep inheriting the ambient section unchanged.
  const groupSection = hasChildren
    ? getSidebarAnalyticsItem(link.pathname)
    : undefined;

  return (
    <div
      className="sidebar-nav-item-wrap"
      data-amp-track-section={groupSection}
    >
      <div
        className={`sidebar-nav-row${isOwnLinkActive ? ' sidebar-nav-row--active' : ''}`}
      >
        {/* Icon + label: navigates */}
        <SidebarNavControl
          analyticsItem={getSidebarAnalyticsItem(link.pathname)}
          analyticsSection={hasChildren ? undefined : analyticsSection}
          className={`sidebar-nav-item-btn${isActive ? ' sidebar-nav-item-btn--active' : ''}`}
          isActive={isOwnLinkActive}
          pathname={link.pathname}
          testId={testId}
          onActivate={onActivate}
        >
          {iconContent}
          <span className="sidebar-nav-item-label">{link.label}</span>
          {link.badge && (
            <span className="sidebar-nav-item-badge">
              <StatusBadge status="informational" text={link.badge} />
            </span>
          )}
        </SidebarNavControl>

        {/* Chevron: toggles sub-items */}
        {hasChildren && (
          <button
            type="button"
            aria-label={intl.formatMessage(
              isExpanded ? messages.collapseSection : messages.expandSection,
              { label: link.label },
            )}
            aria-expanded={isExpanded}
            className="sidebar-chevron-btn"
            data-amp-track-action="toggle-section"
            data-amp-track-item={getSidebarAnalyticsItem(link.pathname)}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((prev: boolean) => !prev);
            }}
          >
            <Icon
              icon={resolveChevronIcon(hasSidebarNavUxExploration, isExpanded)}
              size="s"
            />
          </button>
        )}
      </div>

      {/* Sub-items with hierarchy line */}
      {hasChildren && areChildrenRendered && (
        <div
          ref={childrenRef}
          aria-hidden={isCollapsing}
          className={`sidebar-reveal${isCollapsing ? ' sidebar-reveal--leaving' : ''}`}
        >
          <div className="sidebar-reveal-content sidebar-nav-children-content">
            {/* Vertical line anchored to the icon centre (8px left padding + ~8px half-icon) */}
            <div aria-hidden="true" className="sidebar-nav-children-line" />
            {link.children!.map((child) => (
              <SidebarSubItem
                key={child.pathname}
                link={child}
                activePathname={activePathname}
                pinControl={pinControl}
                onActivate={onActivate}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Ask AI nav item ────────────────────────────────────────────────────────────

interface SidebarAskAiLinkProps {
  isCollapsed: boolean;
  activePathname: string;
  label: string;
}

// Only rendered when the Ask AI entry is actually shown, so useAskAnything()
// (and the setPanelZIndex etc. side effects inside useResolveChatBotEntity)
// don't run for users who don't have access to it.
const SidebarAskAiLink = ({
  isCollapsed,
  activePathname,
  label,
}: SidebarAskAiLinkProps) => {
  const { handleToggleChatbot } = useAskAnything();
  const { isMobile, toggleCollapsed } = useSidebarNav();

  const onActivate = useCallback(() => {
    handleToggleChatbot();
    // Mirrors onNavLinkActivate: close the mobile drawer after acting on a
    // nav entry, same as every other sidebar link.
    if (isMobile) toggleCollapsed();
  }, [handleToggleChatbot, isMobile, toggleCollapsed]);

  return (
    <SidebarNavItem
      link={{ label, pathname: ASK_AI_PATHNAME }}
      isCollapsed={isCollapsed}
      activePathname={activePathname}
      onActivate={onActivate}
      icon="evisort-ai-static"
      testId="sidebar-nav-ask-ai"
    />
  );
};

// ─── Feedback nav item ────────────────────────────────────────────────────────

interface SidebarFeedbackLinkProps {
  isCollapsed: boolean;
  activePathname: string;
}

function SidebarFeedbackLink({
  isCollapsed,
  activePathname,
}: SidebarFeedbackLinkProps) {
  const intl = useIntl();
  const [isModalVisible, setIsModalVisible] = useState(false);

  return (
    <>
      <SidebarNavItem
        analyticsSection="footer"
        link={{
          label: intl.formatMessage(feedbackMessages.open),
          pathname: FEEDBACK_PATHNAME,
        }}
        isCollapsed={isCollapsed}
        activePathname={activePathname}
        onActivate={() => setIsModalVisible(true)}
        icon="comment"
        testId="sidebar-nav-feedback"
      />
      <CLMEvolutionFeedbackModal
        isVisible={isModalVisible}
        onHide={() => setIsModalVisible(false)}
      />
    </>
  );
}

// ─── Pinned rail ──────────────────────────────────────────────────────────────

interface SidebarPinnedRailProps {
  links: NavLink[];
  activePathname: string;
  onActivate: (path: string) => void;
}

/**
 * Pinned entries on the collapsed rail. The 72px rail has no room for the
 * heading, the count, and the drag handles, so the whole section collapses to
 * one "Pinned" row and the entries hang off it in the same flyout the other
 * sections use. The row is a label for that flyout rather than a destination,
 * so activating it does nothing — the flyout is already open by the time a
 * pointer or keyboard has reached it.
 */
function SidebarPinnedRail({
  links,
  activePathname,
  onActivate,
}: SidebarPinnedRailProps) {
  const intl = useIntl();

  if (links.length === 0) return null;

  const handleActivate = (path: string) => {
    if (path === PINNED_PATHNAME) return;
    onActivate(path);
  };

  return (
    <>
      <SidebarNavItem
        analyticsSection="pinned-nav"
        link={{
          label: intl.formatMessage(pinnedMessages.heading),
          pathname: PINNED_PATHNAME,
          // A pinned link that's the active page would otherwise highlight
          // this rail cell too (`testIsActiveLink` matches active children) —
          // it's a label for the flyout, not a destination, so only the
          // link's original section item should show as active.
          neverActive: true,
          children: links,
        }}
        isCollapsed
        activePathname={activePathname}
        collapsedBadge={intl.formatNumber(links.length)}
        icon="pin"
        testId="sidebar-nav-pinned"
        onActivate={handleActivate}
      />
      <div
        aria-hidden="true"
        className="sidebar-section-divider sidebar-section-divider--collapsed"
        data-testid="sidebar-pinned-divider-collapsed"
      />
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const AppSidebarNav = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const user = useCurrentUser();
  const { location } = useRouting();
  const { pathname } = location;

  const { isCollapsed, isMobile, toggleCollapsed } = useSidebarNav();
  const hasClmEvolutionPhase1 = useFlag(FlagType.CLMEvolutionPhase1);
  const hasSidebarNavUxExploration = useFlag(FlagType.SidebarNav);
  const skipMobileTransition = useSuppressTransitionOnChange(isMobile);

  useCandu(user);

  // Side effects only: `SidebarNavControl` renders a `<Link>`, which navigates
  // itself. Runs only for clicks that navigate this tab.
  const onNavLinkActivate = useCallback(() => {
    dispatch(search.actions.reset());
    dispatch(slices.dashboardV2.actions.reset());
    dispatch(slices.searchV3.actions.reset());
    if (isMobile) toggleCollapsed();
  }, [dispatch, isMobile, toggleCollapsed]);

  const { items, homeLink, adminLink, showAskAi } = useNavigationItems({
    onNavLinkClick: onNavLinkActivate,
  });

  const { isPinned, pinnedLinks, reorderPinned, togglePin } =
    useSidebarPinnedLinks(items);

  const backdropRef = useRef<HTMLDivElement>(null);
  const { isLeaving: isBackdropLeaving, isRendered: isBackdropRendered } =
    useRevealPresence(isMobile && !isCollapsed, backdropRef);

  // The collapsed rail shows what is pinned but can't fit the per-row pin
  // affordance, so pinning itself is an expanded-sidebar action.
  const pinControl =
    hasSidebarNavUxExploration && !isCollapsed
      ? { isPinned, togglePin }
      : undefined;

  // Once the collapsed rail scrolls, drop each item's label to buy back
  // vertical space rather than shrinking the icons.
  const navScrollRef = useRef<HTMLElement>(null);
  const isNavScrollOverflowing = useHasVerticalOverflow(
    navScrollRef,
    isCollapsed,
    items.length,
    pinnedLinks.length,
    showAskAi,
    Boolean(homeLink),
  );
  const isNavCondensed = isCollapsed && isNavScrollOverflowing;

  // ─── Skip login routes ─────────────────────────────────────────────────────
  if (
    [
      RoutePathType.Login,
      RoutePathType.LoginEmbed,
      RoutePathType.LoginEmbedSuccess,
    ].includes(location.pathname as RoutePathType)
  ) {
    return null;
  }

  return (
    <>
      {/* Backdrop — tapping it closes the sidebar on mobile. Fades with the
          drawer rather than appearing under it in one frame. */}
      {isBackdropRendered && (
        <div
          ref={backdropRef}
          aria-hidden="true"
          className={`sidebar-backdrop${isBackdropLeaving ? ' sidebar-backdrop--leaving' : ''}`}
          onClick={toggleCollapsed}
        />
      )}
      <aside
        aria-label={intl.formatMessage(messages.sidebarLandmark)}
        aria-hidden={isMobile && isCollapsed ? true : undefined}
        className={`sidebar-aside${isCollapsed ? ' sidebar-aside--collapsed' : ''}${isMobile ? ' sidebar-aside--mobile' : ''}`}
        data-amp-track-region="nav"
        data-amp-track-variant={isCollapsed ? 'collapsed' : 'open'}
      >
        {/* `<aside>` only reserves width; this surface is the visible panel
            and becomes a fixed overlay on mobile, independent of that width. */}
        <div
          className={`sidebar-aside__surface${skipMobileTransition ? ' sidebar-aside__surface--no-transition' : ''}`}
        >
          {isMobile && (
            <div
              className="sidebar-mobile-header"
              data-amp-track-section="header"
              data-testid="sidebar-mobile-header"
            >
              <SidebarNavControl
                analyticsItem="logo-home-link"
                ariaLabel={intl.formatMessage(messages.goHome)}
                className="sidebar-mobile-brand-btn"
                isActive={false}
                pathname={RoutePathType.Home}
                testId="sidebar-mobile-brand-button"
                onActivate={onNavLinkActivate}
              >
                <img
                  alt={intl.formatMessage(messages.brandName)}
                  className="sidebar-mobile-brand-lockup"
                  decoding="sync"
                  fetchPriority="high"
                  height={18}
                  loading="eager"
                  src={brandLockupSrc}
                  width={96}
                />
              </SidebarNavControl>
              {/* Dismisses the drawer. On the desktop sidebar this lives in the
                Workspace row as a panel toggle, but a drawer closes rather than
                collapsing, so here it reads as an X beside the brand. */}
              <button
                type="button"
                aria-label={intl.formatMessage(messages.hideSidebar)}
                className="sidebar-mobile-close"
                data-amp-track-item="sidebar-visibility-toggle"
                data-testid="sidebar-mobile-close"
                title={intl.formatMessage(messages.hideSidebar)}
                onClick={toggleCollapsed}
              >
                <Icon icon="x" size="s" />
              </button>
            </div>
          )}

          {/* ── Workspace header ────────────────────────────────────────────── */}
          <div
            className={`sidebar-workspace-header${isCollapsed ? ' sidebar-workspace-header--collapsed' : ''}`}
            data-amp-track-section="sidebar"
            data-testid="sidebar-workspace-header"
          >
            {!isCollapsed && (
              <span
                className="sidebar-overline"
                data-testid="sidebar-workspace-label"
              >
                {intl.formatMessage(messages.workspaceLabel)}
              </span>
            )}
            {/* The drawer closes from its own header on mobile, so this collapse
              toggle is desktop-only. */}
            {!isMobile && (
              <button
                type="button"
                className="sidebar-panel-toggle"
                aria-label={intl.formatMessage(
                  isCollapsed ? messages.showSidebar : messages.hideSidebar,
                )}
                title={intl.formatMessage(
                  isCollapsed ? messages.showSidebar : messages.hideSidebar,
                )}
                data-amp-track-item="sidebar-visibility-toggle"
                data-testid="sidebar-panel-toggle"
                onClick={toggleCollapsed}
              >
                <Icon
                  icon={isCollapsed ? 'left-panel-open' : 'left-panel-close'}
                  size="s"
                />
              </button>
            )}
          </div>

          {/* ── Nav items ───────────────────────────────────────────────────── */}
          <nav
            ref={navScrollRef}
            aria-label={intl.formatMessage(messages.mainNavLandmark)}
            className={`sidebar-nav-scroll${isCollapsed ? ' sidebar-nav-scroll--collapsed' : ''}${isNavCondensed ? ' sidebar-nav-scroll--condensed' : ''}`}
            data-amp-track-section="primary-nav"
          >
            {isCollapsed ? (
              <div
                aria-hidden="true"
                className="sidebar-section-divider sidebar-section-divider--collapsed"
                data-testid="sidebar-section-divider-top"
              />
            ) : null}
            {homeLink && (
              <SidebarNavItem
                link={homeLink}
                isCollapsed={isCollapsed}
                activePathname={pathname}
                onActivate={onNavLinkActivate}
                icon="home"
                testId="sidebar-nav-home"
              />
            )}
            {showAskAi && (
              <SidebarAskAiLink
                isCollapsed={isCollapsed}
                activePathname={pathname}
                label={intl.formatMessage(messages.askAi)}
              />
            )}
            {/* Divider — sets Home / Ask AI apart as their own top-level section,
              distinct from the Workspace links below. */}
            <div
              aria-hidden="true"
              className={`sidebar-section-divider${isCollapsed ? ' sidebar-section-divider--collapsed' : ''}`}
              data-testid="sidebar-section-divider"
            />
            {hasSidebarNavUxExploration && isCollapsed && (
              <SidebarPinnedRail
                links={pinnedLinks}
                activePathname={pathname}
                onActivate={onNavLinkActivate}
              />
            )}
            {pinControl && (
              <SidebarPinnedSection
                links={pinnedLinks}
                activePathname={pathname}
                onActivate={onNavLinkActivate}
                onReorder={reorderPinned}
                onTogglePin={togglePin}
              />
            )}
            {items.map((link) => (
              <SidebarNavItem
                key={link.pathname}
                link={link}
                isCollapsed={isCollapsed}
                activePathname={pathname}
                pinControl={pinControl}
                onActivate={onNavLinkActivate}
              />
            ))}
          </nav>
          <div
            className={`sidebar-nav-footer${isCollapsed ? ' sidebar-nav-footer--collapsed' : ''}`}
          >
            {hasClmEvolutionPhase1 && (
              <SidebarFeedbackLink
                isCollapsed={isCollapsed}
                activePathname={pathname}
              />
            )}

            {adminLink && (
              <SidebarNavItem
                analyticsSection="footer"
                link={adminLink}
                isCollapsed={isCollapsed}
                activePathname={pathname}
                onActivate={onNavLinkActivate}
                testId="sidebar-nav-admin"
              />
            )}
          </div>
        </div>
      </aside>
    </>
  );
};
